import {HOST} from '../constants';
import {uploadImageFile} from '../service/files.service';
import {dataUrlToFile} from './fileUtils';
import {validateImageSize} from './imageUploadValidation';

const IMAGE_DATA_URL_START = /data:image\/[a-zA-Z0-9.+-]+(?:;[^,;"')\s]+)*;base64,/gi;
const URL_TOKEN = /(?:https?:\/\/|\/\/)[^"'()\s<>,]+|(?<![\w/.-])\/?(?:[\w.-]+\/)*(?:images|videos|previews)\/[^"'()\s<>,]+/gi;
const HTML_MEDIA_START = new RegExp(`${IMAGE_DATA_URL_START.source}|${URL_TOKEN.source}`, 'gi');
const MEDIA_PATH = /^\/?(?:images|videos|previews)\/[^/?#]+$/;

export function isManagedMediaUrl(src: string, mediaBaseUrl = `${HOST}/files`): boolean {
    const path = src.split(/[?#]/)[0];
    if (MEDIA_PATH.test(path)) return true;
    if (!mediaBaseUrl) return false;

    try {
        const base = new URL(mediaBaseUrl);
        const url = new URL(src, `${base.origin}/`);
        if (url.origin !== base.origin) return false;
        if (MEDIA_PATH.test(url.pathname)) return true;
        const prefix = `${base.pathname.replace(/\/$/, '')}/`;
        return url.pathname.startsWith(prefix) && MEDIA_PATH.test(url.pathname.slice(prefix.length));
    } catch {
        return false;
    }
}

type HtmlMediaMatch = {src: string; index: number; end: number};

function imageDataUrlEnd(html: string, start: number): number {
    let end = start;
    while (end < html.length) {
        const code = html.charCodeAt(end);
        const isBase64 =
            (code >= 65 && code <= 90) ||
            (code >= 97 && code <= 122) ||
            (code >= 48 && code <= 57) ||
            code === 43 ||
            code === 47 ||
            code === 61;
        if (isBase64 || code === 10 || code === 13) {
            end += 1;
        } else if (html.slice(end, end + 3).toLowerCase() === ' px') {
            end += 3;
        } else if (html.slice(end, end + 4).toLowerCase() === ' rem') {
            end += 4;
        } else {
            break;
        }
    }
    return end;
}

function findHtmlMedia(html: string, imagesOnly = false): HtmlMediaMatch[] {
    const matcher = new RegExp(imagesOnly ? IMAGE_DATA_URL_START.source : HTML_MEDIA_START.source, 'gi');
    const matches: HtmlMediaMatch[] = [];
    let match = matcher.exec(html);

    while (match !== null) {
        const isDataImage = match[0].toLowerCase().startsWith('data:');
        const end = isDataImage ? imageDataUrlEnd(html, matcher.lastIndex) : matcher.lastIndex;
        if (!isDataImage || end > matcher.lastIndex) {
            matches.push({src: html.slice(match.index, end), index: match.index, end});
        }
        matcher.lastIndex = end;
        match = matcher.exec(html);
    }
    return matches;
}

export function extractImageDataUrls(html: string): string[] {
    return findHtmlMedia(html, true).map(({src}) => src);
}

export function extractManagedMediaUrls(html: string, mediaBaseUrl = `${HOST}/files`): string[] {
    return [
        ...new Set(
            findHtmlMedia(html)
                .map(({src}) => src)
                .filter((src) => isManagedMediaUrl(src, mediaBaseUrl))
        ),
    ];
}

function normalizeMediaUrl(src: string): string {
    if (!src.toLowerCase().startsWith('data:')) return src;
    const commaIndex = src.indexOf(',');
    const metadata = src.slice(0, commaIndex);
    const base64 = src.slice(commaIndex + 1).replace(/\s+/g, '');
    return `${metadata},${base64}`;
}

export async function replaceHtmlMedia(
    html: string,
    convertSrc: (src: string) => Promise<string | null | undefined>,
    mediaBaseUrl = `${HOST}/files`
): Promise<string> {
    const matches = findHtmlMedia(html);
    const sources = new Set(
        matches
            .map(({src}) => src)
            .filter((src) => src.toLowerCase().startsWith('data:') || isManagedMediaUrl(src, mediaBaseUrl))
            .map(normalizeMediaUrl)
    );
    const replacements = new Map<string, string>();

    await Promise.all(
        [...sources].map(async (src) => {
            const converted = await convertSrc(src);
            if (converted && converted !== src) replacements.set(src, converted);
        })
    );

    const parts: string[] = [];
    let cursor = 0;
    matches.forEach(({src, index, end}) => {
        parts.push(html.slice(cursor, index), replacements.get(normalizeMediaUrl(src)) ?? src);
        cursor = end;
    });
    parts.push(html.slice(cursor));
    return parts.join('');
}

export function htmlContainsImageDataUrl(html: string): boolean {
    return extractImageDataUrls(html).length > 0;
}

function extensionForMime(mimeType: string): string {
    if (mimeType === 'image/jpeg') return '.jpg';
    const subtype = mimeType.split('/')[1]?.split('+')[0];
    return subtype ? `.${subtype}` : '.png';
}

export function validateHtmlImageSizes(html: string): void {
    extractImageDataUrls(html).forEach((src) => {
        const base64 = src.slice(src.indexOf(',') + 1).replace(/\s+/g, '');
        const padding = base64.endsWith('==') ? 2 : Number(base64.endsWith('='));
        validateImageSize(Math.floor((base64.length * 3) / 4) - padding);
    });
}

export function createHtmlImageUploader(): (html: string) => Promise<string> {
    const uploads = new Map<string, Promise<string>>();

    return async (html) => {
        validateHtmlImageSizes(html);
        return replaceHtmlMedia(html, (src) => {
            if (!src.toLowerCase().startsWith('data:')) return Promise.resolve(src);
            const existing = uploads.get(src);
            if (existing) return existing;

            const upload = (async () => {
                const mimeMatch = src.slice(0, src.indexOf(',')).match(/^data:([^;,]+)/i);
                const mimeType = mimeMatch?.[1] ?? 'image/png';
                const file = dataUrlToFile(src, `html-image${extensionForMime(mimeType)}`);
                return (await uploadImageFile(file)).url;
            })().catch((error) => {
                uploads.delete(src);
                throw error;
            });

            uploads.set(src, upload);
            return upload;
        });
    };
}

export function replaceHtmlDataImagesWithFileUrls(html: string): Promise<string> {
    return createHtmlImageUploader()(html);
}
